import React, { Suspense } from 'react';
import { Navigate, redirect } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { encryptStorage } from '../encryptStorage';
import { authReducer } from '../redux/slices/authReducer.slice';
import { store } from '../redux/store';
import { LoadingScreen } from '../screens/LoadingScreen';
import { DashboardLayout } from './DashboardLayout';

// Core screens
const MapOverviewScreen = React.lazy(() =>
  import('../screens/MapOverviewScreen/MapOverviewScreen').then(m => ({ default: m.MapOverviewScreen })),
);
const Details = React.lazy(() =>
  import('../screens/MapOverviewScreen/MarkerDetails').then(m => ({ default: m.Details })),
);
const DriversScreen = React.lazy(() =>
  import('../screens/DriversScreen/DriversScreen').then(m => ({ default: m.DriversScreen })),
);
const VehiclesScreen = React.lazy(() => import('../screens/VehiclesScreen').then(m => ({ default: m.VehiclesScreen })));
const AnomaliesScreen = React.lazy(() =>
  import('../screens/AnomaliesScreen').then(m => ({ default: m.AnomaliesScreen })),
);
const NotificationsScreen = React.lazy(() =>
  import('../screens/NotificationsScreen/NotificationsScreen').then(m => ({ default: m.NotificationsScreen })),
);
const ErrorScreen = React.lazy(() => import('../screens/ErrorScreen').then(m => ({ default: m.ErrorScreen })));

// LiveView screens
const LiveViewScreen = React.lazy(() =>
  import('../screens/LiveviewScreen/LiveViewScreen').then(m => ({ default: m.LiveViewScreen })),
);
const LiveViewScreenV2 = React.lazy(() =>
  import('../screens/LiveviewScreen/LiveViewScreenV2').then(m => ({ default: m.LiveViewScreenV2 })),
);

// Safety screens
const SafetyScreen = React.lazy(() =>
  import('../screens/SafetyScreen/SafetyScreen').then(m => ({ default: m.SafetyScreen })),
);
const SafetyDetailsScreen = React.lazy(() =>
  import('../screens/SafetyScreen/SafetyDetailsScreen').then(m => ({ default: m.SafetyDetailsScreen })),
);

// Maintenance Screens
const MaintenanceScreen = React.lazy(() =>
  import('../screens/MaintenanceScreen/MaintenanceScreen').then(m => ({ default: m.MaintenanceScreen })),
);
const MaintenanceScheduleForm = React.lazy(() =>
  import('../screens/MaintenanceScreen/MaintenanceScheduleFormScreen').then(m => ({
    default: m.MaintenanceScheduleForm,
  })),
);
const ViewMaintenanceSchedule = React.lazy(() =>
  import('../screens/MaintenanceScreen/ViewMaintenanceScheduleScreen').then(m => ({
    default: m.ViewMaintenanceSchedule,
  })),
);

//Group Screens
const GroupsScreen = React.lazy(() =>
  import('../screens/GroupsScreen/Groups').then(m => ({ default: m.GroupsScreen })),
);
const ViewManageGroup = React.lazy(() =>
  import('../screens/GroupsScreen/View_ManageGroup').then(m => ({
    default: m.ViewManageGroup,
  })),
);

//Route Optimization Screen
const RoutesScreen = React.lazy(() =>
  import('../screens/RouteOptimizationScreen/RouteScreen').then(m => ({ default: m.RoutesScreen })),
);
const ViewRoute = React.lazy(() =>
  import('../screens/RouteOptimizationScreen/ViewRoute').then(m => ({ default: m.ViewRoute })),
);

// Report screens
const ReportsScreen = React.lazy(() =>
  import('../screens/ReportsScreen/ReportsScreen').then(m => ({ default: m.ReportsScreen })),
);
const ReportSelector = React.lazy(() =>
  import('../screens/ReportsScreen/ReportSelector').then(m => ({ default: m.ReportSelector })),
);
const VehicleReportScreen = React.lazy(() =>
  import('../screens/ReportsScreen/VehicleReportScreen').then(m => ({ default: m.VehicleReportScreen })),
);
const DriverReportScreen = React.lazy(() =>
  import('../screens/ReportsScreen/DriverReportScreen').then(m => ({ default: m.DriverReportScreen })),
);
const MileageReportScreen = React.lazy(() =>
  import('../screens/ReportsScreen/MileageReportScreen').then(m => ({ default: m.MileageReportScreen })),
);
const ResourceUtilizationReportScreen = React.lazy(() =>
  import('../screens/ReportsScreen/ResourceUtilizationScreen').then(m => ({
    default: m.ResourceUtilizationReportScreen,
  })),
);
const HoursOfServiceReportScreen = React.lazy(() =>
  import('../screens/ReportsScreen/HoursOfServiceReportScreen').then(m => ({ default: m.HoursOfServiceReportScreen })),
);
const VehicleDayOnlineScreen = React.lazy(() =>
  import('../screens/ReportsScreen/VehicleDayOnlineScreen').then(m => ({ default: m.VehicleDayOnlineScreen })),
);
const DriverResourceUtilizationReportScreen = React.lazy(() =>
  import('../screens/ReportsScreen/DriverResourceUtilizationScreen').then(m => ({
    default: m.DriverResourceUtilizationReportScreen,
  })),
);
const VehicleResourceUtilizationReportScreen = React.lazy(() =>
  import('../screens/ReportsScreen/VehicleResourceUtilizationScreen').then(m => ({
    default: m.VehicleResourceUtilizationReportScreen,
  })),
);
const DriverPerformanceReportScreen = React.lazy(() =>
  import('../screens/ReportsScreen/DriverPerformanceReportScreen').then(m => ({
    default: m.DriverPerformanceReportScreen,
  })),
);
const MissingDriverReportScreen = React.lazy(() =>
  import('../screens/ReportsScreen/MissingDriverReportScreen').then(m => ({
    default: m.MissingDriverReportScreen,
  })),
);

// Admin screens
const UserManagementScreen = React.lazy(() =>
  import('../screens/AdminScreen/UserManagement').then(m => ({ default: m.UserManagement })),
);

const DeviceActivationScreen = React.lazy(() =>
  import('../screens/AdminScreen/DeviceActivation').then(m => ({ default: m.DeviceActivation })),
);
const VehicleManagementScreen = React.lazy(() =>
  import('../screens/AdminScreen/VehicleManagement').then(m => ({ default: m.VehicleManagement })),
);
const OrganizationManagementScreen = React.lazy(() =>
  import('../screens/AdminScreen/OrganizationManagement').then(m => ({ default: m.OrganizationManagement })),
);
const ReleaseVersionManagementScreen = React.lazy(() =>
  import('../screens/AdminScreen/ReleaseVersionManagement').then(m => ({ default: m.ReleaseVersionManagement })),
);
// Profile screen
const ProfileScreen = React.lazy(() =>
  import('../screens/ProfileScreen/ProfileScreen').then(m => ({ default: m.ProfileScreen })),
);

// const AdminScreen = React.lazy(() => import('../screens/AdminScreen').then(m => ({ default: m.Admin })));

const requireAuth = () => {
  const token = encryptStorage.getItem('token');
  const refreshToken = encryptStorage.getItem('refresh_token');
  if (token) {
    store.dispatch(authReducer.actions.login({ token, refresh_token: refreshToken }));
    return true;
  }
  const currentPath = window.location.pathname;
  const searchParams = window.location.search;
  const fullPath = currentPath && searchParams ? `${currentPath}${searchParams}` : currentPath;
  throw redirect(`${ROUTES.LOGIN}${fullPath ? `?to=${encodeURIComponent(fullPath)}` : ''}`);
};

export const dashboardRoutes = {
  element: <DashboardLayout />,
  errorElement: (
    <Suspense fallback={<LoadingScreen />}>
      <ErrorScreen />
    </Suspense>
  ),
  loader: requireAuth,
  children: [
    {
      path: ROUTES.MAP_OVERVIEW,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <MapOverviewScreen />
        </Suspense>
      ),
      children: [
        {
          path: 'details',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <Details />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: ROUTES.DRIVERS,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <DriversScreen />
        </Suspense>
      ),
    },
    {
      path: ROUTES.VEHICLES,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <VehiclesScreen />
        </Suspense>
      ),
    },
    {
      path: ROUTES.SAFETY_CONCERNS,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <AnomaliesScreen />
        </Suspense>
      ),
    },
    {
      path: ROUTES.LIVEVIEW,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <LiveViewScreen />
        </Suspense>
      ),
    },
    {
      path: ROUTES.LIVEVIEW_V2,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <LiveViewScreenV2 />
        </Suspense>
      ),
    },
    {
      path: ROUTES.NOTIFICATIONS,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <NotificationsScreen />
        </Suspense>
      ),
    },
    {
      path: ROUTES.SAFETY,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <SafetyScreen />
        </Suspense>
      ),
    },
    {
      path: ROUTES.SAFETY_ID,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <SafetyDetailsScreen />
        </Suspense>
      ),
    },
    {
      path: ROUTES.MAINTENANCE,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <MaintenanceScreen />
        </Suspense>
      ),
    },
    {
      path: ROUTES.CREATE_SCHEDULE,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <MaintenanceScheduleForm />
        </Suspense>
      ),
    },
    {
      path: ROUTES.EDIT_SCHEDULE,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <MaintenanceScheduleForm />
        </Suspense>
      ),
    },
    {
      path: ROUTES.VIEW_SCHEDULE,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <ViewMaintenanceSchedule />
        </Suspense>
      ),
    },
    {
      path: ROUTES.PROFILE,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <ProfileScreen />
        </Suspense>
      ),
    },
    {
      path: ROUTES.GROUPS,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <GroupsScreen />
        </Suspense>
      ),
    },
    {
      path: ROUTES.VIEWMANAGEGROUPS,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <ViewManageGroup />
        </Suspense>
      ),
    },
    {
      path: ROUTES.ROUTES_OPTIMIZATION,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <RoutesScreen />
        </Suspense>
      ),
    },
    {
      path: ROUTES.VIEW_ROUTE,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <ViewRoute />
        </Suspense>
      ),
    },
    {
      path: ROUTES.REPORTS,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <ReportsScreen />
        </Suspense>
      ),
      children: [
        {
          path: '',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <ReportSelector />
            </Suspense>
          ),
        },
        {
          path: 'vehicle-summary',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <VehicleReportScreen />
            </Suspense>
          ),
        },
        {
          path: 'driver',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <DriverReportScreen />
            </Suspense>
          ),
        },
        {
          path: 'mileage',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <MileageReportScreen />
            </Suspense>
          ),
        },
        {
          path: 'resource-utilization',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <ResourceUtilizationReportScreen />
            </Suspense>
          ),
        },
        {
          path: 'vehicle-utilization',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <VehicleResourceUtilizationReportScreen />
            </Suspense>
          ),
        },
        {
          path: 'hours-of-service',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <HoursOfServiceReportScreen />
            </Suspense>
          ),
        },
        {
          path: 'daily-vehicle',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <VehicleDayOnlineScreen />
            </Suspense>
          ),
        },
        {
          path: 'driver-utilization',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <DriverResourceUtilizationReportScreen />
            </Suspense>
          ),
        },
        {
          path: 'driver-performance',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <DriverPerformanceReportScreen />
            </Suspense>
          ),
        },
        {
          path: 'missing-driver',
          element: (
            <Suspense fallback={<LoadingScreen />}>
              <MissingDriverReportScreen />
            </Suspense>
          ),
        },
      ],
    },
  ],
};

export const adminRoutes = {
  element: <DashboardLayout />,
  errorElement: (
    <Suspense fallback={<LoadingScreen />}>
      <ErrorScreen />
    </Suspense>
  ),
  loader: requireAuth,
  children: [
    {
      path: ROUTES.ADMIN_USER,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <UserManagementScreen />
        </Suspense>
      ),
    },
    {
      path: ROUTES.ADMIN_DEVICE,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <DeviceActivationScreen />{' '}
        </Suspense>
      ),
    },
    {
      path: ROUTES.ADMIN_VEHICLE,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <VehicleManagementScreen />{' '}
        </Suspense>
      ),
    },
    {
      path: ROUTES.ADMIN_ORGANIZATION,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <OrganizationManagementScreen />{' '}
        </Suspense>
      ),
    },
    {
      path: ROUTES.ADMIN_RELEASE_VERSION,
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <ReleaseVersionManagementScreen />{' '}
        </Suspense>
      ),
    },

    {
      path: '*',
      element: <Navigate to={ROUTES.ADMIN_USER} replace />,
    },
  ],
};
